import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewOverviewDashboard = ({ reviews, onGenerateCaseStudy, transforming, selectedReviewId }) => {
  const stats = {
    total: reviews?.length || 0,
    withCaseStudy: reviews?.filter(r => r?.caseStudyContent)?.length || 0,
    pending: reviews?.filter(r => !r?.caseStudyContent)?.length || 0,
    avgRating: reviews?.length > 0
      ? (reviews?.reduce((sum, r) => sum + (r?.starRating || 0), 0) / reviews?.length)?.toFixed(1)
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="FileText" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{stats?.total}</span>
          </div>
          <p className="text-sm text-muted-foreground">Total Reviews</p>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Sparkles" size={20} className="text-success" />
            <span className="text-2xl font-bold text-foreground">{stats?.withCaseStudy}</span>
          </div>
          <p className="text-sm text-muted-foreground">Case Studies Generated</p>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <span className="text-2xl font-bold text-foreground">{stats?.pending}</span>
          </div>
          <p className="text-sm text-muted-foreground">Pending Transformation</p>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Star" size={20} className="text-amber-500" />
            <span className="text-2xl font-bold text-foreground">{stats?.avgRating}</span>
          </div>
          <p className="text-sm text-muted-foreground">Average Rating</p>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Client Reviews</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rating</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Review</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Metrics</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-muted-foreground">
                    No reviews found
                  </td>
                </tr>
              ) : (
                reviews?.map((review) => (
                  <tr key={review?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4 text-sm text-foreground">
                      <div>
                        <div className="font-medium">{review?.companyName || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{review?.industry || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={14} className="text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-foreground">{review?.starRating}/5</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-foreground max-w-xs truncate">
                      {review?.reviewText}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {review?.complianceSuccessMetrics?.cost_savings_kina && (
                        <div className="text-success font-medium">
                          K{review?.complianceSuccessMetrics?.cost_savings_kina?.toLocaleString()}
                        </div>
                      )}
                      {review?.complianceSuccessMetrics?.time_efficiency_gain_percentage && (
                        <div className="text-xs text-muted-foreground">
                          +{review?.complianceSuccessMetrics?.time_efficiency_gain_percentage}% efficiency
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {review?.caseStudyContent ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                          <Icon name="CheckCircle" size={12} className="mr-1" />
                          Case Study Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                          <Icon name="Clock" size={12} className="mr-1" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {!review?.caseStudyContent && (
                        <Button
                          onClick={() => onGenerateCaseStudy?.(review?.id)}
                          disabled={transforming && selectedReviewId === review?.id}
                          variant="outline"
                          size="sm"
                        >
                          <Icon name="Sparkles" size={14} className="mr-1" />
                          {transforming && selectedReviewId === review?.id ? 'Generating...' : 'Generate'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReviewOverviewDashboard;